import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  ChevronRight,
  ClipboardCheck,
  Download,
  Gift,
  LayoutDashboard,
  Loader2,
  MessageCircle,
  NotebookTabs,
  PlayCircle,
  Printer,
  Salad,
  ShieldCheck,
  Sparkles,
  Target,
  Utensils,
} from 'lucide-react';
import { getBookBundle } from '../data/bookBundles';
import {
  createMetaEventId,
  getMetaBrowserIds,
  trackBookInitiateCheckout,
  trackBookPurchase,
  trackBookViewContent,
} from '../lib/metaPixel';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');
const book = getBookBundle('macros-made-easy-for-women');

const marketplaceCheckoutUrls = {
  clickbank: 'https://localtry44.pay.clickbank.net/?cbitems=MACROS-WOMEN',
  jvzoo: 'https://www.jvzoo.com/b/0/450109/99',
  warriorplus: 'https://warriorplus.com/o2/buy/m04w82/rxrvg2/nbl1vv',
};

const offerOptions = [
  {
    id: 'pdf-app',
    title: 'PDF + app access',
    eyebrow: 'Simple digital option',
    price: 7,
    checkoutLabel: 'Printable PDF + App Access',
    text: 'Read the complete PDF on your phone, tablet, or computer, or print it at home. App access and recipes are included.',
    includes: ['Complete printable PDF', 'Book-code app access', '25 recipes in the app', 'Download and setup support'],
  },
  {
    id: 'complete-bundle',
    title: 'Complete Kindle & e-reader bundle',
    eyebrow: 'Every format included',
    price: book.price,
    checkoutLabel: 'Complete Digital Book Bundle + App Access',
    text: 'Includes everything in the $7 option plus the ebook edition for Kindle, Apple Books, Google Play Books, and compatible e-readers.',
    includes: ['Kindle and e-reader edition', 'Complete printable PDF', 'Book-code app access', '25 recipes in the app', 'Kindle setup instructions'],
  },
];

const bundleComparison = [
  { feature: 'Complete printable PDF', pdfApp: true, complete: true },
  { feature: 'IHJ app access with book code', pdfApp: true, complete: true },
  { feature: '25 recipes inside the app', pdfApp: true, complete: true },
  { feature: 'Kindle and e-reader edition', pdfApp: false, complete: true },
  { feature: 'Apple Books and Google Play Books edition', pdfApp: false, complete: true },
  { feature: 'Kindle and e-reader setup instructions', pdfApp: false, complete: true },
];

const salesImages = {
  cover: '/images/books/macros-sales/book-cover.webp',
  bundle: '/images/books/macros-sales/book-app.webp',
  recipe: '/images/books/macros-sales/recipe-preview.webp',
};

const includedItems = [
  {
    icon: BookOpen,
    title: 'Digital book',
    text: 'A clear beginner system for calories, protein, carbs, fat, hunger, weekends, plateaus, and real-life consistency.',
  },
  {
    icon: Printer,
    title: 'Printable PDF',
    text: 'Keep the guide on your phone or print the worksheets, planning pages, and macro reference sheets.',
  },
  {
    icon: LayoutDashboard,
    title: 'IHJ app access',
    text: 'Use the included book code to open the tracker, macro calculator, recipes, macro snacks, and AI coach.',
  },
  {
    icon: Utensils,
    title: '25 app-ready recipes',
    text: 'Start with meals that already fit the system instead of staring at an empty food log.',
  },
];

const painPoints = [
  'You know protein matters, but you are not sure how much is enough.',
  'Calories feel either too strict or too vague.',
  'Weekends, cravings, and busy days keep blowing up the plan.',
  'You want numbers that guide you without turning meals into a second job.',
];

const appScreens = [
  {
    image: '/images/books/macros-sales/app-dashboard.webp',
    title: 'See today at a glance',
    text: 'Real app screen showing calories, protein, carbs, fat, fiber, and what remains for the day.',
  },
  {
    image: '/images/books/macros-sales/app-food-tracker.webp',
    title: 'Log food without guessing',
    text: 'Real food-tracker screen showing daily totals and macro progress in grams.',
  },
  {
    image: '/images/books/macros-sales/app-ai-coach.webp',
    title: 'Ask the AI coach',
    text: 'Real coach screen with prompts for meals, nutrition labels, recipes, and grocery planning.',
  },
  {
    image: '/images/books/macros-sales/app-macro-snacks.webp',
    title: 'Find macro-friendly snacks',
    text: 'Real snack finder for practical options when protein or other targets need help.',
  },
];

const sections = [
  {
    label: 'Part 1',
    title: 'Macro basics without the noise',
    items: ['Calories and energy balance', 'Protein, carbs, and fats explained plainly', 'How to set a starting target'],
  },
  {
    label: 'Part 2',
    title: 'Build meals you can repeat',
    items: ['Higher-protein meal structure', 'Flexible swaps for busy days', 'How to handle eating out'],
  },
  {
    label: 'Part 3',
    title: 'Track without perfectionism',
    items: ['What to log closely', 'Where “good enough” works', 'How to adjust after real results'],
  },
  {
    label: 'Part 4',
    title: 'Make it sustainable',
    items: ['Plateaus and cravings', 'Weekends and travel', 'Maintenance after weight loss'],
  },
];

const recipeWins = [
  'Protein-first breakfast ideas',
  'Easy bowls and lunches',
  'Dinner templates for families',
  'Snack ideas that actually help your macros',
  'Simple planning prompts',
  'Built-in app recipe access',
];

const accessSteps = [
  {
    icon: ShieldCheck,
    title: 'Check out securely',
    text: 'Choose the $7 PDF + app option or the $19 complete Kindle and e-reader bundle, then check out securely through Stripe.',
  },
  {
    icon: Download,
    title: 'Download your files',
    text: 'After checkout, the files included with your selected bundle and the app instructions are sent to the email used at purchase.',
  },
  {
    icon: ClipboardCheck,
    title: 'Use your app code',
    text: 'Open the book-code login path, enter the included code, and unlock the tracker, calculator, recipes, snacks, and AI coach.',
  },
];

const trustSignals = [
  {
    title: 'A real complete guide',
    text: 'Both options include the complete guide. Choose $19 when you want the Kindle and e-reader edition in addition to the PDF and app.',
  },
  {
    title: 'A real working app',
    text: 'The screens above come from the production app workflow, not a separate demonstration product.',
  },
  {
    title: 'Help from a real inbox',
    text: 'Questions about downloads or a book code go to info@marketnest.org for purchase support.',
  },
];

const faqs = [
  {
    question: 'Is this another strict diet?',
    answer:
      'No. It is a flexible macro system that shows you how to use calories, protein, carbs, and fats as guideposts while still eating normal foods.',
  },
  {
    question: 'Do I need to already know my macros?',
    answer:
      'No. The book explains the basics and the app includes a macro calculator so you can create a realistic starting point.',
  },
  {
    question: 'What do I get after checkout?',
    answer:
      'The $7 option includes the complete printable PDF and app access. The $19 complete bundle also includes the ebook edition for Kindle, Apple Books, Google Play Books, and compatible e-readers.',
  },
  {
    question: 'Will the ebook work on my Kindle?',
    answer:
      'Yes, with the $19 complete bundle. Use Amazon\'s free Send to Kindle service to add the included ebook file to your Kindle library. It can then be read on Kindle e-readers, Fire tablets, and Kindle apps. Simple step-by-step instructions are included.',
  },
  {
    question: 'Can I read it without a Kindle?',
    answer:
      'Yes. Both options include a PDF you can read on any phone, tablet, or computer. The $19 complete bundle also includes the ebook edition for Apple Books, Google Play Books, and compatible ebook readers.',
  },
  {
    question: 'Is the app separate from the book?',
    answer:
      'App access is included with either option. No separate app subscription is required for the included book access.',
  },
  {
    question: 'What if my download or app code does not work?',
    answer:
      'Email info@marketnest.org and we will help with download access, the book-code login path, or app access issues.',
  },
  {
    question: 'Is this medical advice?',
    answer:
      'No. The guide and app provide general nutrition education and tracking tools. They do not diagnose or treat medical conditions.',
  },
];

const SalesPageHeader = ({ restrictExternalLinks = false }) => (
  <header className="sticky top-0 z-40 border-b border-[#d8d6c9] bg-[#fcfbf6]/95 backdrop-blur">
    <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
      {restrictExternalLinks ? (
        <div className="flex items-center gap-2">
          <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-11 w-11 rounded-full object-cover" />
          <span className="text-sm font-black text-[#273526] sm:text-base">Integrative Health Journal</span>
        </div>
      ) : (
        <Link to="/" className="flex items-center gap-2">
        <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-11 w-11 rounded-full object-cover" />
        <span className="text-sm font-black text-[#273526] sm:text-base">Integrative Health Journal</span>
        </Link>
      )}
      <nav className="hidden items-center gap-6 text-sm font-bold text-[#4e5b47] md:flex">
        <a href="#inside" className="hover:text-[#1f2c1e]">Inside</a>
        <a href="#app" className="hover:text-[#1f2c1e]">App</a>
        <a href="#recipes" className="hover:text-[#1f2c1e]">Recipes</a>
        <a href="#faq" className="hover:text-[#1f2c1e]">FAQ</a>
      </nav>
      <a href="#buy" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#253222] px-4 py-2 text-sm font-black text-white hover:bg-[#394833]">
        Get the bundle
        <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  </header>
);

const CheckoutModal = ({ checkoutBook, fetchClientSecret, closeCheckout }) => {
  if (!checkoutBook) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-950/75 px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#6d7b55]">Stripe secure checkout</p>
            <h2 className="mt-1 text-lg font-black text-gray-900">{checkoutBook.title}</h2>
          </div>
          <button type="button" onClick={closeCheckout} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50">
            Close
          </button>
        </div>
        <div className="min-h-[620px] p-4 sm:p-6">
          {stripePromise ? (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">Stripe checkout is missing the publishable key.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const MacrosMadeEasySalesPage = () => {
  const [searchParams] = useSearchParams();
  const [checkoutBook, setCheckoutBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');

  const successSlug = searchParams.get('success');
  const sessionId = searchParams.get('session_id');
  const purchaseEventId = searchParams.get('meta_event_id');
  const marketplace = searchParams.get('checkout')?.toLowerCase() || '';
  const marketplaceCheckoutUrl = marketplaceCheckoutUrls[marketplace] || '';
  const isMarketplaceCheckout = Boolean(marketplaceCheckoutUrl);
  const isJvzooCheckout = marketplace === 'jvzoo';
  const displayedOffers = isJvzooCheckout
    ? [{
      ...offerOptions[1],
      text: 'Includes the complete printable PDF, ebook edition for Kindle, Apple Books, Google Play Books, and compatible e-readers, plus companion app access.',
    }]
    : isMarketplaceCheckout ? [offerOptions[1]] : offerOptions;
  const displayedAccessSteps = isJvzooCheckout
    ? accessSteps.map((step, index) => (index === 0
      ? { ...step, text: 'Get the complete $19 bundle through the secure JVZoo checkout.' }
      : step))
    : accessSteps;
  const displayedTrustSignals = isJvzooCheckout
    ? trustSignals.map((signal, index) => (index === 0
      ? { ...signal, text: 'The $19 bundle includes the complete printable PDF, Kindle and e-reader edition, recipes, and companion app access.' }
      : signal))
    : trustSignals;
  const displayedFaqs = isJvzooCheckout
    ? faqs.map((faq) => (faq.question === 'What do I get after checkout?'
      ? {
        ...faq,
        answer: 'The $19 complete bundle includes the printable PDF, ebook edition for Kindle, Apple Books, Google Play Books, and compatible e-readers, plus companion app access.',
      }
      : faq))
    : faqs;

  const sourceUrl = useMemo(() => {
    if (typeof window === 'undefined') return '/books/macros-made-easy-for-women';
    return `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
  }, []);

  useEffect(() => {
    trackBookViewContent(book);
  }, []);

  useEffect(() => {
    if (marketplace !== 'warriorplus') return undefined;

    const script = document.createElement('script');
    script.src = 'https://warriorplus.com/o2/js/rxrvg2';
    script.defer = true;
    script.dataset.warriorplusOffer = 'rxrvg2';
    document.body.appendChild(script);

    return () => script.remove();
  }, [marketplace]);

  useEffect(() => {
    const pageUrl = `${window.location.origin}/books/macros-made-easy-for-women`;
    const pageTitle = 'Macros Made Easy for Women | Digital Guide + Nutrition App';
    const pageDescription = 'A plain-English macro guide for women available as a printable PDF or a complete Kindle and e-reader bundle with recipes and included nutrition app access.';
    const socialImage = `${window.location.origin}${salesImages.bundle}`;
    const originalTitle = document.title;
    const touched = [];

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      const created = !element;
      if (!element) {
        element = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        const identity = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (identity) element.setAttribute(identity[1], identity[2]);
        document.head.appendChild(element);
      }
      const previous = element.getAttribute(attribute);
      touched.push({ element, attribute, previous, created });
      element.setAttribute(attribute, value);
      return element;
    };

    document.title = pageTitle;
    setMeta('meta[name="description"]', 'content', pageDescription);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDescription);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[property="og:image"]', 'content', socialImage);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDescription);
    setMeta('meta[name="twitter:image"]', 'content', socialImage);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const previousCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', pageUrl);

    const productSchema = document.createElement('script');
    productSchema.type = 'application/ld+json';
    productSchema.dataset.salesProductSchema = 'true';
    productSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: book.title,
      description: pageDescription,
      image: [socialImage, `${window.location.origin}${salesImages.cover}`],
      brand: { '@type': 'Brand', name: 'Integrative Health Journal' },
      offers: offerOptions.map((offer) => ({
        '@type': 'Offer',
        name: offer.title,
        price: offer.price.toFixed(2),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: pageUrl,
      })),
    });
    document.head.appendChild(productSchema);

    return () => {
      document.title = originalTitle;
      touched.forEach(({ element, attribute, previous, created }) => {
        if (created) element.remove();
        else if (previous === null) element.removeAttribute(attribute);
        else element.setAttribute(attribute, previous);
      });
      if (canonicalCreated) canonical.remove();
      else if (previousCanonical === null) canonical.removeAttribute('href');
      else canonical.setAttribute('href', previousCanonical);
      productSchema.remove();
    };
  }, []);

  useEffect(() => {
    if (!successSlug || !sessionId) return;

    let isMounted = true;
    setDeliveryLoading(true);
    setDeliveryError('');

    fetch('/.netlify/functions/get-book-delivery', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not verify your purchase.');
        return data.delivery;
      })
      .then((verifiedDelivery) => {
        if (isMounted) setDelivery(verifiedDelivery);
      })
      .catch((err) => {
        if (isMounted) setDeliveryError(err.message);
      })
      .finally(() => {
        if (isMounted) setDeliveryLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [successSlug, sessionId]);

  useEffect(() => {
    if (!delivery || !sessionId) return;

    const storageKey = `meta-purchase-${sessionId}`;
    if (sessionStorage.getItem(storageKey)) return;

    const purchasedOffer = offerOptions.find((offer) => offer.id === delivery.offerType) || offerOptions[0];
    trackBookPurchase({ ...book, title: `${book.title} - ${purchasedOffer.title}`, price: purchasedOffer.price }, purchaseEventId || sessionId);
    sessionStorage.setItem(storageKey, 'tracked');
  }, [delivery, purchaseEventId, sessionId]);

  const fetchClientSecret = useCallback(async () => {
    if (!checkoutBook) throw new Error('Choose a book before opening checkout.');

    const res = await fetch('/.netlify/functions/create-book-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slug: checkoutBook.slug,
        offer: checkoutBook.offerId || 'complete-bundle',
        metaEventId: checkoutBook.metaEventId,
        metaBrowserIds: getMetaBrowserIds(),
        sourceUrl,
        returnUrl: `${window.location.origin}/books/macros-made-easy-for-women?success=${checkoutBook.slug}&offer=${checkoutBook.offerId || 'complete-bundle'}&session_id={CHECKOUT_SESSION_ID}&meta_event_id=${checkoutBook.metaEventId}`,
      }),
    });
    const data = await res.json();

    if (!res.ok || !data.client_secret) throw new Error(data.error || 'Checkout is unavailable right now.');
    return data.client_secret;
  }, [checkoutBook, sourceUrl]);

  const startCheckout = (offer = offerOptions[0]) => {
    const metaEventId = createMetaEventId(`book-${book.slug}-${offer.id}`);
    const checkoutMetaBook = { ...book, title: `${book.title} - ${offer.title}`, price: offer.price };
    setError('');
    setLoading(true);
    trackBookInitiateCheckout(checkoutMetaBook, metaEventId);
    if (marketplaceCheckoutUrl) {
      window.location.assign(marketplaceCheckoutUrl);
      return;
    }
    setCheckoutBook({
      ...book,
      title: `${book.title} - ${offer.title}`,
      price: offer.price,
      checkoutLabel: offer.checkoutLabel,
      offerId: offer.id,
      metaEventId,
    });
  };

  const checkoutAction = (offer, className, children) => {
    if (marketplaceCheckoutUrl) {
      return (
        <a
          href={marketplaceCheckoutUrl}
          onClick={() => {
            const metaEventId = createMetaEventId(`book-${book.slug}-${offer.id}`);
            const checkoutMetaBook = { ...book, title: `${book.title} - ${offer.title}`, price: offer.price };
            trackBookInitiateCheckout(checkoutMetaBook, metaEventId);
          }}
          className={className}
        >
          {children}
        </a>
      );
    }

    return (
      <button type="button" onClick={() => startCheckout(offer)} disabled={loading} className={className}>
        {children}
      </button>
    );
  };

  const closeCheckout = () => {
    setCheckoutBook(null);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#fcfbf6] text-[#20251f]">
      <SalesPageHeader restrictExternalLinks={isJvzooCheckout} />

      <main>
        <section className="overflow-hidden border-b border-[#dfd9c8] bg-[#f6f2e8]">
          <div className="mx-auto grid min-h-[calc(100vh-64px)] max-w-7xl items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_0.92fr] lg:py-12">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#c8c0a7] bg-white px-4 py-2 text-sm font-black text-[#526044]">
                <Sparkles className="h-4 w-4" />
                Ebook + printable PDF + IHJ app access
              </div>
              <div className="mt-6 grid grid-cols-[92px_1fr] items-center gap-4 sm:grid-cols-[118px_1fr] lg:block">
                <img
                  src={salesImages.cover}
                  alt="Macros Made Easy for Women book cover"
                  className="aspect-[2/3] w-full rounded-lg object-cover shadow-lg ring-1 ring-black/10 lg:hidden"
                  width="600"
                  height="960"
                  fetchpriority="high"
                />
                <h1 className="text-4xl font-black leading-[1.02] tracking-normal text-[#243021] sm:text-6xl lg:text-7xl">
                  Macros Made Easy for Women
                </h1>
              </div>
              <p className="mt-5 max-w-2xl text-xl font-semibold leading-9 text-[#596250]">
                Stop guessing how much to eat. Learn clear macro targets, build flexible meals, and use the included app to make the plan practical.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                {checkoutAction(
                  offerOptions[1],
                  'inline-flex items-center justify-center gap-2 rounded-full bg-[#263521] px-7 py-4 text-base font-black text-white shadow-xl shadow-[#263521]/20 hover:bg-[#3a4a33] disabled:opacity-70',
                  <>
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Gift className="h-5 w-5" />}
                  Get the complete bundle for ${offerOptions[1].price}
                  </>,
                )}
                {!isMarketplaceCheckout && (
                  <button
                    type="button"
                    onClick={() => startCheckout(offerOptions[0])}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#bdb59f] bg-white px-7 py-4 text-base font-black text-[#283426] hover:bg-[#fbf8ef] disabled:opacity-70"
                  >
                    <Download className="h-5 w-5" />
                    PDF + app ${offerOptions[0].price}
                  </button>
                )}
                <a href="#watch" className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-black text-[#354332] underline decoration-[#9da78b] underline-offset-4 hover:text-[#1f2c1e]">
                  <PlayCircle className="h-5 w-5" />
                  Watch the app workflow
                </a>
              </div>
              <p className="mt-4 max-w-2xl text-sm font-bold leading-6 text-[#4e5b47]">
                {isMarketplaceCheckout
                  ? 'This $19 offer includes every format, Kindle and e-reader files, the printable PDF, and companion app access. No subscription is required for the included book access.'
                  : 'Choose $19 for every format, including Kindle and e-readers. Choose $7 when you only need the printable PDF and app. No subscription is required for the included book access.'}
              </p>
              <div className="mt-8 grid gap-3 text-sm font-bold text-[#4e5b47] sm:grid-cols-3">
                {['Instant digital delivery', isJvzooCheckout ? 'Secure JVZoo checkout' : 'Secure Stripe checkout', 'Book-code app access'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-[#697a45]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="grid items-center gap-5 sm:grid-cols-[0.72fr_1fr]">
                <div className="mx-auto max-w-[280px] sm:max-w-none">
                  <img src={salesImages.cover} alt={`${book.title} book cover`} className="aspect-[2/3] w-full rounded-xl object-cover shadow-2xl shadow-black/25 ring-1 ring-black/10" width="600" height="960" />
                </div>
                <div className="space-y-4">
                  <img src={salesImages.bundle} alt="Macros Made Easy book and app bundle" className="w-full rounded-xl object-cover shadow-xl ring-1 ring-black/10" width="720" height="1280" />
                  <img src={salesImages.recipe} alt="Preview of the 25 recipes included with app access" className="aspect-[2/1] w-full rounded-xl object-cover shadow-md ring-1 ring-black/10" width="560" height="560" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {!isJvzooCheckout && (
        <section className="border-b border-[#dfd9c8] bg-[#243021] px-4 py-14 text-white sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#c6d58c]">Choose your format</p>
              <h2 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">Exactly what each option includes</h2>
              <p className="mt-4 text-base leading-7 text-[#d8dfd3] sm:text-lg">
                Both options include the complete guide and app access. The $19 full bundle adds the Kindle and e-reader edition.
              </p>
            </div>

            <div className="mt-8 overflow-hidden rounded-lg border border-white/20 bg-white text-[#243021] shadow-2xl">
              <div className="grid grid-cols-[1.25fr_0.8fr_0.8fr] border-b border-[#d9dece] bg-[#f4f6ef]">
                <div className="p-3 text-xs font-black uppercase text-[#596250] sm:p-5 sm:text-sm">Included</div>
                <div className="border-l border-[#d9dece] p-3 text-center sm:p-5">
                  <p className="text-base font-black sm:text-xl">PDF + app</p>
                  <p className="mt-1 text-2xl font-black">$7</p>
                </div>
                <div className="border-l border-[#d9dece] bg-[#e9efd7] p-3 text-center sm:p-5">
                  <p className="text-base font-black sm:text-xl">Full bundle</p>
                  <p className="mt-1 text-2xl font-black">$19</p>
                </div>
              </div>

              {bundleComparison.map((row) => (
                <div key={row.feature} className="grid grid-cols-[1.25fr_0.8fr_0.8fr] border-b border-[#e4e6de] last:border-b-0">
                  <div className="flex items-center p-3 text-xs font-bold leading-5 sm:p-5 sm:text-base">{row.feature}</div>
                  <div className="flex items-center justify-center border-l border-[#e4e6de] p-3 text-center sm:p-5">
                    {row.pdfApp ? (
                      <span className="inline-flex flex-col items-center gap-1 text-xs font-black text-[#536343] sm:flex-row sm:text-sm">
                        <CheckCircle className="h-5 w-5" /> Included
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-[#7b8078] sm:text-sm">Not included</span>
                    )}
                  </div>
                  <div className="flex items-center justify-center border-l border-[#e4e6de] bg-[#f7f9f1] p-3 text-center sm:p-5">
                    {row.complete ? (
                      <span className="inline-flex flex-col items-center gap-1 text-xs font-black text-[#536343] sm:flex-row sm:text-sm">
                        <CheckCircle className="h-5 w-5" /> Included
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-[#7b8078] sm:text-sm">Not included</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              {checkoutAction(
                offerOptions[1],
                'inline-flex items-center justify-center gap-2 rounded-full bg-[#c6d58c] px-7 py-4 text-base font-black text-[#243021] hover:bg-[#d7e4a7] disabled:opacity-70',
                <>
                <BookOpen className="h-5 w-5" />
                Get the full bundle for $19
                </>,
              )}
              {!isMarketplaceCheckout && (
                <button
                  type="button"
                  onClick={() => startCheckout(offerOptions[0])}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-4 text-base font-black text-white hover:bg-white/15 disabled:opacity-70"
                >
                  <Download className="h-5 w-5" />
                  Get PDF + app for $7
                </button>
              )}
            </div>
          </div>
        </section>
        )}

        <section className="border-b border-[#dfd9c8] bg-white px-4 py-14 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.78fr_1fr] lg:items-center">
            <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-2xl border border-[#ded8c8] bg-[#f7f2e7] p-7 shadow-xl shadow-[#2f3a2b]/10">
              <img
                src={salesImages.cover}
                alt="Macros Made Easy for Women published by Integrative Health Journal"
                className="mx-auto aspect-[2/3] w-full max-w-[260px] rounded-xl object-cover shadow-2xl"
                loading="lazy"
                width="600"
                height="960"
              />
              <p className="mt-5 text-center text-sm font-black text-[#526044]">Published by Integrative Health Journal</p>
            </div>
            <div>
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">Behind the guide</p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">Practical nutrition education, published with a working companion app.</h2>
              <p className="mt-5 text-lg leading-8 text-[#5b6556]">
                Macros Made Easy for Women is published by Integrative Health Journal under the author name Ava Mitchell. It was created for women who want clear explanations and useful tools, not an extreme plan or an anonymous download.
              </p>
              <p className="mt-4 text-lg leading-8 text-[#5b6556]">
                The guide explains the system. The included app handles the daily work: calculate targets, log meals, review progress, find recipes and snacks, and ask the AI coach practical questions.
              </p>
              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                {[
                  ['Plain-English macro education', BookOpen],
                  ['Flexible food-first guidance', Salad],
                  ['Book plus app support', ShieldCheck],
                ].map(([label, Icon]) => (
                  <div key={label} className="rounded-xl border border-[#ded8c8] bg-[#fcfbf6] p-4">
                    <Icon className="h-6 w-6 text-[#536343]" />
                    <p className="mt-3 text-sm font-black leading-5 text-[#303a2e]">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {successSlug && (
          <section className="border-b border-emerald-200 bg-emerald-50 px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-5xl rounded-2xl border border-emerald-200 bg-white p-5 text-emerald-900">
              <div className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-black">Payment received.</p>
                  {deliveryLoading && <p className="mt-1 text-sm">Verifying your purchase and preparing downloads...</p>}
                  {deliveryError && <p className="mt-1 text-sm">Your payment was received. If downloads do not appear here, check the email used at checkout.</p>}
                  {delivery && (
                    <div className="mt-4">
                      <p className="text-sm font-semibold">{delivery.title} is ready.</p>
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <a href={delivery.files.pdf} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-gray-800">
                          <Download className="h-4 w-4" />
                          PDF
                        </a>
                        {delivery.files.epub && (
                          <a href={delivery.files.epub} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#536343] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#435236]">
                            <BookOpen className="h-4 w-4" />
                            Kindle &amp; e-reader edition
                          </a>
                        )}
                      </div>
                      {delivery.appAccess && (
                        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                          <p className="text-sm font-bold">Included app access code</p>
                          <p className="mt-1 font-mono text-2xl font-black tracking-[0.2em]">{delivery.appAccess.code}</p>
                          <Link to="/login?tab=book" className="mt-2 inline-flex text-sm font-bold text-emerald-700 underline">
                            Open app access page
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {error && (
          <section className="px-4 pt-6 sm:px-6">
            <div className="mx-auto max-w-5xl rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">{error}</div>
          </section>
        )}

        <section className="px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">The problem</p>
                <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">Macros should make eating clearer, not make your life smaller.</h2>
                <p className="mt-5 text-lg leading-8 text-[#5b6556]">
                  This bundle was built for the woman who wants structure without food rules taking over the day.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {painPoints.map((point) => (
                  <div key={point} className="rounded-xl border border-[#ded8c8] bg-white p-5 shadow-sm">
                    <Target className="h-6 w-6 text-[#6b774e]" />
                    <p className="mt-4 text-base font-bold leading-7 text-[#303a2e]">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="inside" className="border-y border-[#dfd9c8] bg-white px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">A+ content preview</p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">Everything in the bundle works together.</h2>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {includedItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="rounded-xl border border-[#e0dbce] bg-[#fcfbf6] p-5">
                    <Icon className="h-7 w-7 text-[#536343]" />
                    <h3 className="mt-4 text-lg font-black text-[#243021]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#596250]">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="watch" className="bg-[#1f2b1e] px-4 py-16 text-white sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_0.7fr] lg:items-center">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#c6d58c]">Book to app workflow</p>
              <h2 className="mt-3 text-4xl font-black leading-tight">Watch how the system moves from reading to daily tracking.</h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-[#d8dfd3]">
                The app access is the bridge: calculate targets, log meals, ask the AI coach for practical next steps, and use the book recipes without rebuilding everything from scratch.
              </p>
            </div>
            <div className="overflow-hidden rounded-2xl border border-white/15 bg-black shadow-2xl">
              <video className="aspect-video w-full object-cover" controls muted playsInline preload="none" poster={salesImages.bundle}>
                <source src="/meta/ads/macros-made-easy/macros-made-easy-app-ad.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </section>

        <section id="app" className="px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-9 max-w-3xl text-center">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">Real app screens</p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">See the tools included with your book code.</h2>
              <p className="mt-4 text-lg leading-8 text-[#5b6556]">These screens come from the actual Integrative Health Journal app workflow buyers receive.</p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {appScreens.map((screen) => (
                <article key={screen.title} className="overflow-hidden rounded-2xl border border-[#ddd6c7] bg-white shadow-sm">
                  <div className="h-[430px] overflow-hidden bg-[#f4f5f7] p-3">
                    <img src={screen.image} alt={screen.title} className="h-full w-full rounded-xl object-cover object-top shadow-sm" loading="lazy" width="640" height="1385" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-black text-[#243021]">{screen.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-[#5b6556]">{screen.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-[#dfd9c8] bg-[#f6f2e8] px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">Inside the guide</p>
                <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">A clear path from “I should track” to “I know what to do today.”</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {sections.map((section) => (
                  <div key={section.title} className="rounded-xl border border-[#d9d2c2] bg-white p-5 shadow-sm">
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-[#7c875a]">{section.label}</p>
                    <h3 className="mt-2 text-xl font-black text-[#243021]">{section.title}</h3>
                    <ul className="mt-4 space-y-2">
                      {section.items.map((item) => (
                        <li key={item} className="flex gap-2 text-sm font-semibold leading-6 text-[#505b4b]">
                          <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-[#6b774e]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="recipes" className="px-4 py-16 sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">Recipes included</p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">Start with meals that fit the system.</h2>
              <p className="mt-5 text-lg leading-8 text-[#5b6556]">
                The recipe bonus makes the first week easier. Readers can open the app, find the book recipes, and stop starting from a blank screen.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {recipeWins.map((item) => (
                  <div key={item} className="flex items-center gap-2 rounded-xl border border-[#ded8c8] bg-white px-4 py-3 text-sm font-black text-[#303a2e]">
                    <Salad className="h-5 w-5 text-[#536343]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <img src={salesImages.recipe} alt="Preview of recipes included with Macros Made Easy" className="w-full rounded-2xl object-cover shadow-xl ring-1 ring-black/10" loading="lazy" width="560" height="560" />
          </div>
        </section>

        <section className="border-y border-[#dfd9c8] bg-white px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">Before you buy</p>
                <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">Here is exactly what happens after checkout.</h2>
                <p className="mt-5 text-lg leading-8 text-[#5b6556]">
                  You are not buying a mystery download or hoping the app works later. The bundle is built around a simple checkout, download, and book-code access flow.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {displayedAccessSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className="rounded-xl border border-[#ded8c8] bg-[#fcfbf6] p-5 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <Icon className="h-7 w-7 text-[#536343]" />
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#243021] text-sm font-black text-white">{index + 1}</span>
                      </div>
                      <h3 className="mt-5 text-lg font-black text-[#243021]">{step.title}</h3>
                      <p className="mt-3 text-sm leading-6 text-[#5b6556]">{step.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-8 rounded-2xl border border-[#c9d3b8] bg-[#f3f7ed] p-6">
              <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr] lg:items-center">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.2em] text-[#536343]">Purchase support included</p>
                  <h3 className="mt-2 text-2xl font-black text-[#243021]">Your download instructions, app code, and support contact arrive together.</h3>
                  <p className="mt-3 text-base leading-7 text-[#4e5b47]">
                    Keep the email used at checkout. For questions about a download or book code, contact info@marketnest.org and include that email address.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    isJvzooCheckout ? 'Secure JVZoo checkout' : 'Secure Stripe checkout',
                    'Immediate delivery page',
                    'Book-code app access',
                    'Email purchase support',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-black text-[#303a2e]">
                      <CheckCircle className="h-5 w-5 flex-shrink-0 text-[#536343]" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f6f2e8] px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">What you can verify now</p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-[#243021]">A complete guide, a real app, and a clear support path.</h2>
              <p className="mt-5 text-lg leading-8 text-[#5b6556]">
                The offer is specific about what is included so you can decide before checkout. Reader reviews will only be added when they come from real purchasers who give permission to share them.
              </p>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {displayedTrustSignals.map((signal) => (
                <div key={signal.title} className="rounded-xl border border-[#ded8c8] bg-white p-6 shadow-sm">
                  <MessageCircle className="h-7 w-7 text-[#536343]" />
                  <h3 className="mt-4 text-xl font-black leading-7 text-[#243021]">{signal.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#5b6556]">{signal.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="buy" className="bg-[#243021] px-4 py-16 text-white sm:px-6">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#c6d58c]">Ready when you are</p>
              <h2 className="mt-3 text-4xl font-black leading-tight">Get the complete Macros Made Easy bundle.</h2>
              <p className="mt-5 text-lg leading-8 text-[#d8dfd3]">
                {isMarketplaceCheckout
                  ? 'Get every format, including Kindle and e-readers, plus the printable PDF and included app access for $19.'
                  : 'Choose the $7 PDF + app option, or get every format including Kindle and e-readers for $19.'}
              </p>
              <p className="mt-4 rounded-xl border border-white/15 bg-white/10 p-4 text-sm font-bold leading-6 text-[#edf4e7]">
                Includes download and book-code support.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {displayedOffers.map((offer) => (
                <div key={offer.id} className="rounded-2xl border border-[#c6d58c] bg-white p-6 text-[#243021] shadow-2xl">
                  <div className="flex items-start gap-4">
                    <img src={salesImages.cover} alt={`${book.title} cover`} className="aspect-[2/3] w-24 rounded-lg object-cover shadow-lg" width="600" height="960" loading="lazy" />
                    <div className="flex-1">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#7c875a]">{offer.eyebrow}</p>
                      <h3 className="mt-1 text-xl font-black leading-6 text-[#243021]">{offer.title}</h3>
                      <p className="mt-2 text-4xl font-black">${offer.price}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm font-semibold leading-6 text-[#5b6556]">{offer.text}</p>
                  <ul className="mt-4 space-y-2">
                    {offer.includes.map((item) => (
                      <li key={item} className="flex gap-2 text-sm font-bold text-[#4d5848]">
                        <CheckCircle className="h-5 w-5 flex-shrink-0 text-[#536343]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {checkoutAction(
                    offer,
                    'mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#253222] px-6 py-4 text-base font-black text-white hover:bg-[#394833] disabled:opacity-70',
                    <>
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
                    Get the complete bundle for ${offer.price}
                    </>,
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="px-4 py-16 sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="text-center">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-[#6b774e]">FAQ</p>
              <h2 className="mt-3 text-4xl font-black text-[#243021]">Quick answers before checkout.</h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {displayedFaqs.map((faq) => (
                <div key={faq.question} className="rounded-xl border border-[#ded8c8] bg-white p-5 shadow-sm">
                  <MessageCircle className="h-6 w-6 text-[#536343]" />
                  <h3 className="mt-3 text-lg font-black text-[#243021]">{faq.question}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#5b6556]">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#dfd9c8] bg-white px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-7xl text-sm text-[#5b6556]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-semibold">Integrative Health Journal</p>
            <div className="flex flex-wrap gap-5 font-bold">
              {!isJvzooCheckout && <Link to="/books" className="hover:text-[#243021]">All books</Link>}
              {!isJvzooCheckout && <Link to="/login?tab=book" className="hover:text-[#243021]">I have a book code</Link>}
              <a href="#inside" className="hover:text-[#243021]">What is included</a>
              <Link to="/book-purchase-details" className="hover:text-[#243021]">Purchase & privacy</Link>
              <a href="mailto:info@marketnest.org?subject=Macros%20Made%20Easy%20support" className="hover:text-[#243021]">Purchase support</a>
            </div>
          </div>
          <div className="mt-8 space-y-4 border-t border-[#dfd9c8] pt-6 text-base leading-7">
            <p>
              Disclaimer: Please note that this product does not provide any guarantee of income or success. The results achieved by the product owner or any other individuals mentioned are not indicative of future success or earnings. This website is not affiliated with FaceBook or any of its associated entities. Once you navigate away from FaceBook, the responsibility for the content and its usage lies solely with the user. All content on this website, including but not limited to text, images, and multimedia, is protected by copyright law and the Digital Millennium Copyright Act. Unauthorized copying, duplication, modification, or theft, whether intentional or unintentional, is strictly prohibited. Violators will be prosecuted to the fullest extent of the law.
            </p>
            <p>
              We want to clarify that JVZoo serves as the retailer for the products featured on this site. JVZoo® is a registered trademark of BBC Systems Inc., a Florida corporation located at 1809 E. Broadway Street, Suite 125, Oviedo, FL 32765, USA, and is used with permission. The role of JVZoo as a retailer does not constitute an endorsement, approval, or review of these products or any claims, statements, or opinions used in their promotion.
            </p>
          </div>
          {isJvzooCheckout && (
            <img
              src="https://i.jvzoo.com/0/450109/99"
              alt="Macros Made Easy for Women + IHJ App"
              width="1"
              height="1"
              className="h-px w-px"
            />
          )}
        </div>
      </footer>

      <CheckoutModal checkoutBook={checkoutBook} fetchClientSecret={fetchClientSecret} closeCheckout={closeCheckout} />
    </div>
  );
};

export default MacrosMadeEasySalesPage;
